import type {
  AdminAuditEvent,
  ChecklistTemplateAdmin,
  ContractorProfile,
  EmailRecipientPolicy,
  InspectionCaseSummary,
  UserSummary,
  VehicleProfile,
} from './admin-api';
import {
  actorTypeLabel,
  duplicateFieldLabel,
  emailPolicyStatusLabel,
  emailScopeKindLabel,
  eventTypeLabel,
  formatYekaterinburg,
  subjectTypeLabel,
} from './admin-presentation';
import {
  caseBusinessLabel,
  templateBusinessLabel,
  userBusinessLabel,
} from './template-admin-presentation';

export { caseBusinessLabel, templateBusinessLabel, userBusinessLabel };

export const REFERENCE_SCOPE_HAS_APPROVED_SELECTOR = false;
export const REFERENCE_DATA_HAS_SCHEMA_METADATA = false;
export const RUBITIME_PROVIDER_CATALOGUE_EXISTS = false;
export const RUBITIME_JOURNAL_API_REACHABLE = false;

export const MISSING_USER_LABEL = 'Пользователь недоступен';
export const UNIDENTIFIED_VEHICLE_LABEL = 'ТС без идентификатора';
export const ANY_TEMPLATE_LABEL = 'Любой шаблон';
export const ANY_CONTRACTOR_LABEL = 'Любой подрядчик';
export const ANY_CONTEXT_LABEL = 'Любой контекст';
export const ANY_EVENT_LABEL = 'Все события';
export const ANY_CASE_LABEL = 'Все осмотры';
export const MISSING_TEMPLATE_LABEL = 'Шаблон недоступен';
export const MISSING_CONTRACTOR_LABEL = 'Подрядчик недоступен';
export const MISSING_CASE_LABEL = 'Осмотр недоступен';

const SECRET_KEY_PATTERN = /(password|secret|token|smtp|api[_-]?key|rk|private[_-]?key|webhook)/i;

export type JsonObjectParseResult =
  | { readonly ok: true; readonly value: Record<string, unknown> }
  | { readonly ok: false; readonly message: string };

export type OptionalJsonObjectParseResult =
  | { readonly ok: true; readonly value: Record<string, unknown> | null }
  | { readonly ok: false; readonly message: string };

export function contractorBusinessLabel(
  profile: Pick<ContractorProfile, 'legal_name' | 'inn' | 'status'>,
): string {
  const name = profile.legal_name.trim().length > 0 ? profile.legal_name : MISSING_CONTRACTOR_LABEL;
  const inn = profile.inn !== null && profile.inn.trim().length > 0 ? ` · ИНН ${profile.inn}` : '';
  const archived = profile.status === 'ARCHIVED' ? ' · В архиве' : '';
  return `${name}${inn}${archived}`;
}

export function vehicleBusinessLabel(
  profile: Pick<VehicleProfile, 'registration_plate' | 'vin' | 'alternative_identifier'>,
): string {
  const plate = profile.registration_plate?.trim() ?? '';
  if (plate.length > 0) {
    return plate;
  }
  const vin = profile.vin?.trim() ?? '';
  if (vin.length > 0) {
    return `VIN ${vin}`;
  }
  const alt = profile.alternative_identifier?.trim() ?? '';
  if (alt.length > 0) {
    return alt;
  }
  return UNIDENTIFIED_VEHICLE_LABEL;
}

const VEHICLE_MODEL_KEYS = ['model', 'vehicle_model', 'mark_model'] as const;

function attributeString(
  attributes: Record<string, unknown> | null | undefined,
  key: string,
): string {
  if (attributes === null || attributes === undefined) {
    return '';
  }
  const value = attributes[key];
  return typeof value === 'string' ? value : '';
}

/** Read model with precedence: model → vehicle_model → mark_model. */
export function readVehicleModel(attributes: Record<string, unknown> | null | undefined): string {
  for (const key of VEHICLE_MODEL_KEYS) {
    const value = attributeString(attributes, key).trim();
    if (value.length > 0) {
      return value;
    }
  }
  return '';
}

/**
 * Write model into the first existing precedence key; if none exist and value is
 * non-empty, write attributes.model. Clearing removes all precedence keys.
 */
export function writeVehicleModel(
  attributes: Record<string, unknown>,
  model: string,
): Record<string, unknown> {
  const next = { ...attributes };
  const trimmed = model.trim();
  const existingKey = VEHICLE_MODEL_KEYS.find((key) =>
    Object.prototype.hasOwnProperty.call(attributes, key),
  );
  if (trimmed.length === 0) {
    for (const key of VEHICLE_MODEL_KEYS) {
      delete next[key];
    }
    return next;
  }
  if (existingKey !== undefined) {
    next[existingKey] = trimmed;
    return next;
  }
  next.model = trimmed;
  return next;
}

export function readVehicleColor(attributes: Record<string, unknown> | null | undefined): string {
  return attributeString(attributes, 'color').trim();
}

export function writeVehicleColor(
  attributes: Record<string, unknown>,
  color: string,
): Record<string, unknown> {
  const next = { ...attributes };
  const trimmed = color.trim();
  if (trimmed.length === 0) {
    delete next.color;
  } else {
    next.color = trimmed;
  }
  return next;
}

export function readContractorRepresentative(
  attributes: Record<string, unknown> | null | undefined,
): string {
  return attributeString(attributes, 'representative').trim();
}

export function readContractorPhone(
  attributes: Record<string, unknown> | null | undefined,
): string {
  return attributeString(attributes, 'phone').trim();
}

export function writeContractorContactAttributes(
  attributes: Record<string, unknown>,
  contact: { readonly representative: string; readonly phone: string },
): Record<string, unknown> {
  const next = { ...attributes };
  const representative = contact.representative.trim();
  const phone = contact.phone.trim();
  if (representative.length === 0) {
    delete next.representative;
  } else {
    next.representative = representative;
  }
  if (phone.length === 0) {
    delete next.phone;
  } else {
    next.phone = phone;
  }
  return next;
}

/** List/detail primary: model · plate/VIN/alt when model is present. */
export function vehicleIdentityLabel(
  profile: Pick<
    VehicleProfile,
    'registration_plate' | 'vin' | 'alternative_identifier' | 'attributes'
  >,
): string {
  const identity = vehicleBusinessLabel(profile);
  const model = readVehicleModel(profile.attributes);
  if (model.length === 0) {
    return identity;
  }
  return `${model} · ${identity}`;
}

export function vehicleSecondaryLabel(
  profile: Pick<VehicleProfile, 'registration_plate' | 'vin' | 'alternative_identifier'>,
): string | null {
  const parts: string[] = [];
  const vin = profile.vin?.trim() ?? '';
  if ((profile.registration_plate?.trim() ?? '').length > 0 && vin.length > 0) {
    parts.push(`VIN ${vin}`);
  }
  const alt = profile.alternative_identifier?.trim() ?? '';
  if (
    alt.length > 0 &&
    parts.length === 0 &&
    (profile.registration_plate?.trim() ?? '').length > 0
  ) {
    parts.push(alt);
  }
  return parts.length === 0 ? null : parts.join(' · ');
}

export function aliasesLabel(aliases: readonly string[]): string {
  return aliases.filter((item) => item.trim().length > 0).join(', ');
}

export function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

export function parseJsonObject(text: string, fieldLabel: string): JsonObjectParseResult {
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, message: `${fieldLabel} должен быть JSON-объектом.` };
    }
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch {
    return { ok: false, message: `${fieldLabel}: некорректный JSON.` };
  }
}

export function parseOptionalJsonObject(
  text: string,
  fieldLabel: string,
): OptionalJsonObjectParseResult {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return { ok: true, value: null };
  }
  return parseJsonObject(trimmed, fieldLabel);
}

export function parseRecipientList(text: string): readonly string[] {
  return text
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function formatRecipientList(recipients: readonly string[]): string {
  return recipients.join('\n');
}

export function splitAliases(text: string): readonly string[] {
  return text
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export interface PayloadTreeRow {
  readonly path: string;
  readonly value: string;
}

export function payloadTreeRows(value: unknown, path = ''): readonly PayloadTreeRow[] {
  if (value === null || value === undefined) {
    return [{ path: path.length === 0 ? '—' : path, value: 'null' }];
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return [{ path: path.length === 0 ? '—' : path, value: '[]' }];
    }
    return value.flatMap((item, index) =>
      payloadTreeRows(item, path.length === 0 ? `[${index}]` : `${path}[${index}]`),
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return [{ path: path.length === 0 ? '—' : path, value: '{}' }];
    }
    return entries.flatMap(([key, nested]) =>
      payloadTreeRows(nested, path.length === 0 ? key : `${path}.${key}`),
    );
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [{ path: path.length === 0 ? '—' : path, value: String(value) }];
  }
  return [{ path: path.length === 0 ? '—' : path, value: String(value) }];
}

export function isSecretLikeKey(key: string): boolean {
  return SECRET_KEY_PATTERN.test(key);
}

export function redactPayloadForDisplay(
  payload: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (payload === null || payload === undefined) {
    return {};
  }
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (isSecretLikeKey(key)) {
      continue;
    }
    if (typeof value === 'string' && SECRET_KEY_PATTERN.test(value)) {
      continue;
    }
    result[key] = value;
  }
  return result;
}

export function redactedPayloadSummary(
  payload: Record<string, unknown> | null | undefined,
): string {
  if (payload === null || payload === undefined) {
    return 'Нет краткого описания изменений';
  }
  const parts: string[] = [];
  for (const [key, value] of Object.entries(payload)) {
    if (isSecretLikeKey(key)) {
      continue;
    }
    if (typeof value === 'string' && SECRET_KEY_PATTERN.test(value)) {
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      parts.push(`${key}: ${String(value)}`);
    } else if (value === null) {
      parts.push(`${key}: —`);
    }
    if (parts.length >= 4) {
      break;
    }
  }
  return parts.length === 0 ? 'Нет краткого описания изменений' : parts.join(' · ');
}

export function policyListLabel(
  policy: EmailRecipientPolicy,
  templates: ReadonlyMap<string, ChecklistTemplateAdmin>,
  contractors: ReadonlyMap<string, ContractorProfile>,
): string {
  const kind = emailScopeKindLabel(policy.scope_kind);
  const status = emailPolicyStatusLabel(policy.status);
  const selectors = policySelectorSummary(policy, templates, contractors);
  return `${kind} · ${status} · приоритет ${policy.priority} · ${selectors}`;
}

export function policySelectorSummary(
  policy: Pick<
    EmailRecipientPolicy,
    'scope_kind' | 'template_id' | 'contractor_organization_profile_id' | 'reference_scope'
  >,
  templates: ReadonlyMap<string, ChecklistTemplateAdmin>,
  contractors: ReadonlyMap<string, ContractorProfile>,
): string {
  if (policy.scope_kind === 'GLOBAL') {
    return 'любые условия';
  }
  return [
    templateSelectorLabel(policy.template_id, templates),
    contractorSelectorLabel(policy.contractor_organization_profile_id, contractors),
    referenceScopeLabel(policy.reference_scope),
  ].join(' · ');
}

export function templateSelectorLabel(
  templateId: string | null,
  templates: ReadonlyMap<string, ChecklistTemplateAdmin>,
): string {
  if (templateId === null) {
    return ANY_TEMPLATE_LABEL;
  }
  const template = templates.get(templateId);
  return template === undefined ? MISSING_TEMPLATE_LABEL : templateBusinessLabel(template);
}

export function contractorSelectorLabel(
  contractorId: string | null,
  contractors: ReadonlyMap<string, ContractorProfile>,
): string {
  if (contractorId === null) {
    return ANY_CONTRACTOR_LABEL;
  }
  const profile = contractors.get(contractorId);
  return profile === undefined ? MISSING_CONTRACTOR_LABEL : contractorBusinessLabel(profile);
}

export function referenceScopeLabel(value: Record<string, unknown> | null): string {
  return value === null ? ANY_CONTEXT_LABEL : 'Задан экспертный контекст';
}

export function duplicateFieldsLabel(fields: readonly string[]): string {
  return fields.map((field) => `${duplicateFieldLabel(field)} (${field})`).join(', ');
}

export function actorBusinessLabel(
  event: Pick<AdminAuditEvent, 'actor_type' | 'actor_user_id'>,
  users: ReadonlyMap<string, UserSummary>,
): string {
  if (event.actor_user_id === null) {
    return actorTypeLabel(event.actor_type);
  }
  const user = users.get(event.actor_user_id);
  if (user === undefined) {
    return MISSING_USER_LABEL;
  }
  return userBusinessLabel(user);
}

export function subjectBusinessLabel(
  event: Pick<AdminAuditEvent, 'subject_type' | 'subject_id'>,
  lookup: {
    readonly contractors: ReadonlyMap<string, ContractorProfile>;
    readonly vehicles: ReadonlyMap<string, VehicleProfile>;
    readonly templates: ReadonlyMap<string, ChecklistTemplateAdmin>;
    readonly cases: ReadonlyMap<string, InspectionCaseSummary>;
    readonly users: ReadonlyMap<string, UserSummary>;
  },
): string {
  const category = subjectTypeLabel(event.subject_type);
  if (event.subject_id === null) {
    return category;
  }
  const contractor = lookup.contractors.get(event.subject_id);
  if (event.subject_type === 'CONTRACTOR_ORGANIZATION_PROFILE' && contractor !== undefined) {
    return contractorBusinessLabel(contractor);
  }
  const vehicle = lookup.vehicles.get(event.subject_id);
  if (event.subject_type === 'VEHICLE_EQUIPMENT_PROFILE' && vehicle !== undefined) {
    return vehicleIdentityLabel(vehicle);
  }
  const template = lookup.templates.get(event.subject_id);
  if (event.subject_type === 'CHECKLIST_TEMPLATE' && template !== undefined) {
    return templateBusinessLabel(template);
  }
  const inspection = lookup.cases.get(event.subject_id);
  if (event.subject_type === 'INSPECTION_CASE' && inspection !== undefined) {
    return caseBusinessLabel(inspection);
  }
  const user = lookup.users.get(event.subject_id);
  if (event.subject_type === 'USER' && user !== undefined) {
    return userBusinessLabel(user);
  }
  return category;
}

export function auditPrimarySummary(event: AdminAuditEvent): string {
  return `${eventTypeLabel(event.event_type)} · ${formatYekaterinburg(event.occurred_at)}`;
}

export function inspectionCaseSelectorLabel(row: InspectionCaseSummary): string {
  return caseBusinessLabel(row);
}

export function toIdMap<T extends { readonly id: string }>(items: readonly T[]): Map<string, T> {
  return new Map(items.map((item) => [item.id, item]));
}

export function attributesPreserved(
  original: Record<string, unknown>,
  edited: Record<string, unknown> | null,
): Record<string, unknown> {
  return edited === null ? original : edited;
}
